import type { WebEditorLayoutProject } from '../layout.js';
import { editorImageBreakpointKeys, resolveEditorImageScale } from '../geometry.js';
import { resolveTypographySizes } from '../typography-scale.js';
import type { WebEditorFieldKind } from '../element-definition.js';
import { autoLayoutNodeClasses, autoLayoutNodeStyles } from './auto-layout.js';

export interface WebEditorRenderOptions {
  project: WebEditorLayoutProject;
  mode?: 'live' | 'editor';
  selectedElementId?: string;
  inlineEditingElementId?: string;
  elementKinds?: Record<string, WebEditorFieldKind>;
  onElementSelect?: (elementId: string) => void;
  onElementPointerDown?: (event: PointerEvent, elementId: string) => void;
  onAddTestimonial?: () => void;
  onNavigate?: (href: string) => void;
  currentPath?: string;
  // Hero-fan section keys whose cards should render flipped (CTA side) so the back text is
  // visible and editable in the WYSIWYG preview. Toggled from the section toolbar.
  flippedFanSections?: ReadonlySet<string>;
}

export const editorNodeIdClass = (elementId: string) => `vp-editor-id-${elementId.replace(/[^a-zA-Z0-9_-]/g, '_')}`;

export const editorNodeClass = (editor: WebEditorRenderOptions | undefined, elementId: string, baseClass = '') => {
  const classes = [baseClass];
  const frame = editor?.project.elementFrames[elementId];
  classes.push(...autoLayoutNodeClasses(frame));
  if (resolveTypographySizes(editor?.project.settings?.typography, frame)) classes.push('vp-responsive-type');
  if (frame?.gridContainerDesktop) classes.push('vp-responsive-grid-parent-desktop');
  if (frame?.gridContainerMobile) classes.push('vp-responsive-grid-parent-mobile');
  if (frame?.gridDesktop) classes.push('vp-responsive-grid-item-desktop');
  if (frame?.gridMobile) classes.push('vp-responsive-grid-item-mobile');
  if (frame?.imageFocusDesktop || frame?.imageFocusMobile) classes.push('vp-responsive-image-focus');
  if (
    frame?.imageScaleDesktop !== undefined ||
    frame?.imageScaleMobile !== undefined ||
    frame?.imageScaleByBreakpoint !== undefined ||
    frame?.imageMaskDesktop !== undefined ||
    frame?.imageMaskMobile !== undefined
  )
    classes.push('vp-responsive-image-transform');
  if (editor?.mode === 'editor') {
    classes.push('vp-editor-node', editorNodeIdClass(elementId));
    const kind = editor.elementKinds?.[elementId];
    if (kind) classes.push(`vp-editor-${kind}-node`);
    if (editor.selectedElementId === elementId) classes.push('is-selected');
    if (editor.inlineEditingElementId === elementId) classes.push('is-inline-editing');
  } else if (frame?.href || Object.values(frame?.inlineTextStyles ?? {}).some(styles => styles?.length)) {
    // Live pages keep the id only where a runtime enhancement (link or selected-word typography)
    // needs a stable lookup target.
    classes.push(editorNodeIdClass(elementId));
    if (frame?.href) classes.push('vp-has-link');
  }
  return classes.filter(Boolean).join(' ');
};

export const editorNodeStyle = (editor: WebEditorRenderOptions | undefined, elementId: string) => {
  const frame = editor?.project.elementFrames[elementId];
  if (!frame) return '';

  const styles: string[] = [];
  styles.push(...autoLayoutNodeStyles(frame));
  const kind = editor?.elementKinds?.[elementId];
  if (frame.hidden) styles.push('display: none !important;');
  if (frame.x || frame.y) styles.push(`position: relative; transform: translate3d(${frame.x}px, ${frame.y}px, 0);`);
  if (frame.zIndex !== undefined) styles.push(`z-index: ${frame.zIndex};`);
  if (frame.width !== undefined) styles.push(`width: ${frame.width}px;`);
  if (frame.height !== undefined) styles.push(`height: ${frame.height}px;`);
  if (frame.opacity !== undefined) styles.push(`opacity: ${frame.opacity};`);
  if (frame.color) {
    styles.push(`color: ${frame.color}; --vp-icon-color: ${frame.color};`);
  }
  if (frame.backgroundColor)
    styles.push(`background-color: ${frame.backgroundColor}; --vp-heading-highlight-color: ${frame.backgroundColor};`);
  if (kind === 'icon' && frame.color) styles.push(`background-color: ${frame.color};`);
  if (frame.fontWeight !== undefined) styles.push(`font-weight: ${frame.fontWeight};`);
  const typography = resolveTypographySizes(editor?.project.settings?.typography, frame);
  if (typography) {
    if (typography.desktop !== undefined) styles.push(`--vp-font-size-desktop: ${typography.desktop}px;`);
    if (typography.mobile !== undefined) styles.push(`--vp-font-size-mobile: ${typography.mobile}px;`);
  }
  if (frame.textAlign) styles.push(`text-align: ${frame.textAlign};`);
  if (frame.borderRadius !== undefined) styles.push(`border-radius: ${frame.borderRadius}px;`);
  if (frame.objectFit) styles.push(`object-fit: ${frame.objectFit};`);
  if (frame.gridColumnsDesktop !== undefined) styles.push(`--vp-grid-columns-desktop: ${frame.gridColumnsDesktop};`);
  if (frame.gridColumnsMobile !== undefined) styles.push(`--vp-grid-columns-mobile: ${frame.gridColumnsMobile};`);
  if (frame.gridDesktop) {
    styles.push(`--vp-grid-start-desktop: ${frame.gridDesktop.columnStart};`);
    styles.push(`--vp-grid-span-desktop: ${frame.gridDesktop.columnSpan};`);
    styles.push(`--vp-grid-order-desktop: ${frame.gridDesktop.order};`);
    if (frame.gridDesktop.maskWidth !== undefined)
      styles.push(`--vp-grid-width-desktop: ${frame.gridDesktop.maskWidth}px;`);
    if (frame.gridDesktop.maskHeight !== undefined)
      styles.push(`--vp-grid-height-desktop: ${frame.gridDesktop.maskHeight}px;`);
  }
  if (frame.gridMobile) {
    styles.push(`--vp-grid-start-mobile: ${frame.gridMobile.columnStart};`);
    styles.push(`--vp-grid-span-mobile: ${frame.gridMobile.columnSpan};`);
    styles.push(`--vp-grid-order-mobile: ${frame.gridMobile.order};`);
    if (frame.gridMobile.maskWidth !== undefined)
      styles.push(`--vp-grid-width-mobile: ${frame.gridMobile.maskWidth}px;`);
    if (frame.gridMobile.maskHeight !== undefined)
      styles.push(`--vp-grid-height-mobile: ${frame.gridMobile.maskHeight}px;`);
  }
  if (frame.imageFocusDesktop)
    styles.push(`--vp-image-focus-x-desktop: ${frame.imageFocusDesktop.x}%; --vp-image-focus-y-desktop: ${frame.imageFocusDesktop.y}%;`);
  if (frame.imageFocusMobile)
    styles.push(`--vp-image-focus-x-mobile: ${frame.imageFocusMobile.x}%; --vp-image-focus-y-mobile: ${frame.imageFocusMobile.y}%;`);
  const imageMaskDesktop = frame.imageMaskDesktop === true;
  const imageMaskMobile = frame.imageMaskMobile ?? imageMaskDesktop;
  const clipInset = (scale: number) => ((1 - 1 / Math.max(0.01, scale)) * 50).toFixed(4);
  if (
    frame.imageScaleDesktop !== undefined ||
    frame.imageScaleMobile !== undefined ||
    frame.imageScaleByBreakpoint !== undefined ||
    frame.imageMaskDesktop !== undefined ||
    frame.imageMaskMobile !== undefined
  ) {
    editorImageBreakpointKeys.forEach(key => {
      const scale = resolveEditorImageScale(frame, key) / 100;
      const masked = key === 'mobile' ? imageMaskMobile : imageMaskDesktop;
      styles.push(`--vp-image-scale-${key}: ${scale}; --vp-image-clip-${key}: ${masked ? `inset(${clipInset(scale)}%)` : 'none'};`);
    });
  }
  return styles.join(' ');
};

// Navigate to a link target configured on an arbitrary element (in-page #anchor, internal path or
// external URL). Mirrors how the navigation/anchor links behave elsewhere on the page.
const navigateElementLink = (href: string) => {
  if (typeof window === 'undefined') return;
  const target = href.trim();
  if (!target) return;

  if (target.startsWith('#')) {
    const element = document.querySelector<HTMLElement>(target);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', target);
    return;
  }

  if (/^(mailto:|tel:)/i.test(target)) {
    window.location.href = target;
    return;
  }

  if (/^https?:\/\//i.test(target)) {
    const sameOrigin = (() => {
      try {
        return new URL(target).origin === window.location.origin;
      } catch {
        return false;
      }
    })();
    if (sameOrigin) window.location.href = target;
    else window.open(target, '_blank', 'noopener');
    return;
  }

  // Relative internal path (e.g. /ablauf).
  window.location.href = target;
};

// Delegated click handler for the published page: turns any element the editor linked into a link.
// A no-op unless at least one element actually has a configured href, so it is safe to always attach.
export const elementLinkClickHandler = (editor: WebEditorRenderOptions | undefined) => (event: MouseEvent) => {
  if (!editor || editor.mode === 'editor') return;
  const frames = editor.project.elementFrames;
  const origin = event.target as HTMLElement | null;
  if (!origin) return;
  // Never hijack genuinely interactive children (their own behaviour wins).
  if (origin.closest('a[href], button, input, textarea, select, [contenteditable="true"]')) return;
  const linked = origin.closest('.vp-has-link') as HTMLElement | null;
  if (!linked) return;
  const id = Object.keys(frames).find(key => frames[key]?.href && linked.classList.contains(editorNodeIdClass(key)));
  const href = id ? frames[id]?.href : undefined;
  if (!href) return;
  event.preventDefault();
  navigateElementLink(href);
};

export const elementLinkKeyDownHandler = (editor: WebEditorRenderOptions | undefined) => (event: KeyboardEvent) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const origin = event.target as HTMLElement | null;
  if (!origin?.matches('[data-vp-editor-link="true"]')) return;
  event.preventDefault();
  const mouseEvent = new MouseEvent('click', { bubbles: true, cancelable: true });
  origin.dispatchEvent(mouseEvent);
};

export const syncLinkedElementAccessibility = (project: WebEditorLayoutProject) => {
  if (typeof document === 'undefined') return;

  document.querySelectorAll<HTMLElement>('[data-vp-editor-link="true"]').forEach(node => {
    node.removeAttribute('data-vp-editor-link');
    node.removeAttribute('role');
    node.removeAttribute('tabindex');
  });

  for (const [elementId, frame] of Object.entries(project.elementFrames)) {
    if (!frame.href) continue;
    const selector = `.${editorNodeIdClass(elementId)}`;
    document.querySelectorAll<HTMLElement>(selector).forEach(node => {
      if (node.matches('a[href], button, input, textarea, select')) return;
      node.dataset.vpEditorLink = 'true';
      node.setAttribute('role', 'link');
      node.tabIndex = 0;
      if (!node.getAttribute('aria-label')) {
        node.setAttribute('aria-label', node.textContent?.trim() || frame.href || elementId);
      }
    });
  }
};

export const editorNodePointerDown = (
  editor: WebEditorRenderOptions | undefined,
  elementId: string,
  event: PointerEvent,
) => {
  if (editor?.mode !== 'editor') return;
  const target = event.target as HTMLElement | null;
  // Let real links finish their click. Selecting on pointerdown rerenders the preview before the
  // browser can dispatch click, which makes navigation appear broken inside the WYSIWYG iframe.
  if (target?.closest('a[href]') && editor.onNavigate) return;
  if (target?.closest('[contenteditable="true"], [contenteditable="plaintext-only"]')) {
    event.stopPropagation();
    editor.onElementSelect?.(elementId);
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  editor.onElementSelect?.(elementId);
  editor.onElementPointerDown?.(event, elementId);
};

export const editorNodeClick = (
  editor: WebEditorRenderOptions | undefined,
  elementId: string,
  event: MouseEvent,
) => {
  if (editor?.mode !== 'editor') return;
  const target = event.target as HTMLElement | null;
  if (target?.closest('[contenteditable="true"], [contenteditable="plaintext-only"]')) {
    event.stopPropagation();
    editor.onElementSelect?.(elementId);
    return;
  }
  const link = target?.closest('a[href]') as HTMLAnchorElement | null;
  const href = link?.getAttribute('href');
  if (href && editor.onNavigate) {
    event.preventDefault();
    event.stopPropagation();
    editor.onElementSelect?.(elementId);
    editor.onNavigate(href);
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  editor.onElementSelect?.(elementId);
};
